import type { User } from '@droidmap/user-service-contract'

/**
 * Local-backend dev mode.
 *
 * Enabled by running the web dev server with `VITE_LOCAL_API=true`
 * (see `bin/start-local-backend.sh`). When on:
 *  - service clients talk to the locally-running fleet on localhost ports
 *    (see {@link LOCAL_SERVICE_PORTS}) instead of cloud dev, and
 *  - the Cognito login is bypassed entirely — the local services run with
 *    mock auth, so the app signs in as a fixed {@link LOCAL_USER}.
 *
 * The default `nx serve web` (flag unset) is unaffected and still points at
 * cloud dev with real Cognito.
 */
export const IS_LOCAL_API = import.meta.env.VITE_LOCAL_API === 'true'

/**
 * Base of the 100-port window this environment's local stack occupies, injected
 * by `bin/start-local-web.sh` as `VITE_LOCAL_PORT_BASE`. Each branch/worktree
 * gets a distinct slot (and therefore base) so several full stacks run on one
 * host without clashing. Defaults to slot 0's base for a bare `nx serve web`.
 *
 * Keep in sync with `PORT_BASE_START` in `bin/local-env-slot.sh`.
 */
const LOCAL_PORT_BASE = Number(import.meta.env.VITE_LOCAL_PORT_BASE ?? 20000)

/**
 * Offset of each backend service within the env's port window. Keys are the
 * service subdomains used by {@link createServiceEndpoint}.
 *
 * `tile` is the Python FastAPI tile-service run locally by
 * bin/start-local-backend.sh (basemap token + asset COG tiles).
 *
 * MUST stay in sync with the service offsets in `bin/local-env-slot.sh`.
 */
const LOCAL_PORT_OFFSETS: Record<string, number> = {
  asset: 1,
  user: 2,
  organisation: 3,
  permissions: 4,
  'mapping-job': 5,
  token: 6,
  project: 7,
  tile: 8,
  geometry: 9,
  admin: 10,
}

/**
 * Port each backend service listens on for this environment, derived from
 * {@link LOCAL_PORT_BASE}. Used by {@link createServiceEndpoint}.
 */
export const LOCAL_SERVICE_PORTS: Record<string, number> = Object.fromEntries(
  Object.entries(LOCAL_PORT_OFFSETS).map(([service, offset]) => [
    service,
    LOCAL_PORT_BASE + offset,
  ]),
)

/**
 * Host the local fleet is reached on: whatever host served the page, so the
 * app works both from `localhost` and from another device hitting this machine
 * by LAN/tailnet name. Falls back to `localhost` outside a browser (tests,
 * SSR).
 */
export const localApiHost = (): string =>
  typeof window === 'undefined' ? 'localhost' : window.location.hostname

/** Local tile-service base URL (used for raster COG tile URLs in local dev). */
export const LOCAL_TILE_ENDPOINT = `http://${localApiHost()}:${LOCAL_SERVICE_PORTS.tile}`

/**
 * Fixed user identity for local-backend dev mode. Sent on every request as the
 * calling user (`x-droidmap-calling-user-id`); the local services' mock auth
 * honours that header so every service process scopes data to the same user.
 *
 * The active org is NOT fixed here — the user creates one through the normal
 * in-app flow (which mints a server-side ULID, exactly as in cloud) and it is
 * sent as `x-droidmap-org` thereafter. Permission checks run for real locally,
 * so the create-org flow is what grants this user access.
 *
 * Must be a valid ULID — the services validate id headers/params with
 * `z.ulid()` — so it's a recognisable 26-char Crockford-base32 sentinel rather
 * than a free-form string.
 */
export const LOCAL_USER_ID = '0000000000000000000DEV0SER'

/**
 * A fully-populated mock user so the app renders without a real Cognito
 * session. The epoch timestamps are placeholders — nothing in local dev
 * depends on them.
 *
 * `VITE_LOCAL_USER_FIRST_NAME` / `VITE_LOCAL_USER_LAST_NAME` change the name the
 * app header shows. The docs captures use them to show a neutral demo identity
 * (see `packages/marketing/docs-capture`). The id stays fixed because the
 * backend's mock auth and the seeded grants are keyed on it.
 */
export const LOCAL_USER: User = {
  id: LOCAL_USER_ID,
  userEmail: 'local@droidmap.dev',
  username: 'localdev',
  firstName: import.meta.env.VITE_LOCAL_USER_FIRST_NAME ?? 'Local',
  lastName: import.meta.env.VITE_LOCAL_USER_LAST_NAME ?? 'Dev',
  created: new Date(0),
  createdBy: LOCAL_USER_ID,
  updated: new Date(0),
  updatedBy: LOCAL_USER_ID,
}
